/**
 * NRIC display masking (REQ-C-033).
 *
 * Mirrors the backend's `mask_nric`
 * (`src/hana/security/nric_encryption.py::mask_nric`) on the validated
 * NRIC-shaped happy path — the only path this helper runs on (NRIC
 * fields are NRIC_REGEX-validated before submit): show the prefix
 * letter + the last 3 digits + the suffix letter, masking the middle 4
 * digits with `****`. Divergence on non-NRIC input is deliberate: the
 * backend falls back to `"****" + last4`; this helper returns the value
 * unchanged so non-NRIC ack-bubble fields render as typed.
 *
 *   S1234567A -> S****567A
 *   S8488999P -> S****999P
 *
 * This is a DISPLAY-PATH helper — it runs at the moment the widget
 * builds the patient-side acknowledgement bubble summarising a
 * submitted form. It is NOT a capture-path transform: the value the
 * user typed is sent to the backend verbatim in the FORM_DATA wire
 * frame (the backend owns encrypt-at-rest), and only the on-screen
 * summary is masked. See `.claude/rules/pdpa-capture-vs-display-scope.md`
 * MUST Rule 2 (mask at egress / display, never at ingress / capture).
 *
 * Backend pattern (verbatim):
 *   _NRIC_MASK_PATTERN = ([STFGMstfgm])\d{4}(\d{3}[A-Za-z])
 *   mask         -> `{group1}****{group2}`
 *   non-NRIC text is returned unchanged.
 */

/**
 * NRIC/FIN: a prefix letter (S/T/F/G/M, either case), 4 digits to mask,
 * then the trailing 3 digits + suffix letter to preserve. Matches the
 * backend `_NRIC_MASK_PATTERN` so widget + backend produce identical
 * masked forms for the same input.
 */
const NRIC_MASK_PATTERN = /([STFGMstfgm])\d{4}(\d{3}[A-Za-z])/;

/**
 * Mask a single NRIC-shaped token. Non-NRIC strings are returned
 * unchanged (the helper is intent-scoped to NRIC-typed field values —
 * it does NOT mask phone numbers, names, or free text).
 *
 *   maskNric("S8488999P") === "S****999P"
 *   maskNric("Jane Tan")  === "Jane Tan"  (untouched)
 */
export function maskNric(value: string): string {
  if (!value) return value;
  const match = NRIC_MASK_PATTERN.exec(value.trim());
  if (!match) return value;
  return `${match[1]}****${match[2]}`;
}
